import { Box, Typography, Checkbox } from '@material-ui/core'
import Accordion from '@material-ui/core/Accordion'
import AccordionDetails from '@material-ui/core/AccordionActions'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import Chip from '@material-ui/core/Chip'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox'
import { Stack } from '@mui/material'
import classNames from 'classnames'
import React from 'react'
import Fade from 'react-reveal/Fade'

export const STYLE_BASE_ALIGN = {
  padding: '0px',
  minHeight: '20px !important',
}

export enum FilterStateEnum {
  FULL = 'FULL',
  NONE = 'NONE',
  PARTIAL = 'PARTIAL',
}

export interface IToggleState {
  label: string
  state: keyof typeof FilterStateEnum
}

interface ISidePanelItemProps {
  actions?: React.ReactNode
  children: React.ReactChild | any[] | boolean
  chip?: string
  className?: string
  expandable?: boolean
  expanded?: boolean
  filterState?: keyof typeof FilterStateEnum
  noWrap?: boolean
  onDeselectAll?: () => void
  onSelectAll?: () => void
  onToggleExpanded?: (expanded: boolean) => void
  subtitle?: string
  title?: string
}

const ExpansionPanelSummary = withStyles({
  root: STYLE_BASE_ALIGN,
  content: {
    overflow: 'hidden',
  },
  expandIcon: {
    padding: 0,
    width: '12px',
    height: '12px',
  },
})(AccordionSummary)

const ExpansionPanelDetails = withStyles({
  root: STYLE_BASE_ALIGN,
})(AccordionDetails)

export const ExpansionIcon = withStyles({
  root: {
    fontSize: 25,
    color: '#192233',
  },
})(ExpandMoreIcon)

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      boxSizing: 'border-box',
      borderBottom: `1px solid #EBEDF3`,
      padding: '20px 30px 16px 20px',
    },
    chip: {
      height: '20px',
      marginLeft: theme.spacing(2),
      fontWeight: 'bold',
    },
    title: {
      marginTop: 1,
    },
    content: {
      padding: '0 20px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'inherit',
      justifyContent: 'inherit',
    },
    expandRoot: {
      padding: 0,
      margin: 0,
      width: '100%',
    },
    expandSummary: {
      width: '100%',
      padding: 0,
      margin: 0,
      alignItems: 'center',
    },
    expandContent: {
      margin: '0 !important',
      minHeight: '50px',
    },
    expandIcon: {
      marginTop: 3,
    },
    selectStatusText: {
      '& .MuiInputBase-root.Mui-disabled': {
        color: theme.palette.text.primary,
      },
    },
    checkbox: {
      padding: 0,
    },
  })

const SidePanelItem = (props: WithStyles<typeof styles> & ISidePanelItemProps) => {
  const {
    children,
    classes,
    className,
    title = '',
    subtitle = '',
    chip = '',
    expandable = false,
    expanded = true,
    onToggleExpanded,
    onDeselectAll,
    onSelectAll,
    noWrap = true,
    filterState = undefined,
    actions,
  } = props

  const handleChecked = function (checked: boolean) {
    if (checked) {
      onSelectAll?.()
    } else {
      onDeselectAll?.()
    }
  }

  return (
    <Box display="flex" className={classNames(classes.root, className)}>
      <Accordion
        expanded={expanded}
        onChange={(_, newExpanded) => onToggleExpanded && onToggleExpanded(newExpanded)}
        className={classes.expandRoot}
        square
        elevation={0}
      >
        <ExpansionPanelSummary
          classes={{ root: classes.expandSummary, content: classes.expandContent, expandIcon: classes.expandIcon }}
          expandIcon={expandable ? <ExpansionIcon /> : <span />}
        >
          {!!filterState && (
            // TODO: Make a proper component to replace the current one
            <Box minWidth="18px" marginRight="7px">
              <Checkbox
                key={'select-all'}
                data-entry-id={'select-all'}
                color="primary"
                className={classes.checkbox}
                checked={filterState === FilterStateEnum.FULL}
                indeterminate={filterState === FilterStateEnum.PARTIAL}
                indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                onChange={(_e, checked) => handleChecked(checked)}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          )}
          <Box display="flex" flexDirection="column" width="100%">
            <Typography variant="h5" className={classes.title} noWrap={noWrap} title={title}>
              {title}
            </Typography>
            {subtitle && (
              <Box display="flex" minHeight="15px">
                <Fade when={!expanded} delay={100}>
                  <Typography variant="h6" noWrap={noWrap} title={subtitle}>
                    {subtitle}
                  </Typography>
                </Fade>
              </Box>
            )}
          </Box>
          <Stack direction="row" alignItems="center">
            {actions && <Box>{actions}</Box>}
            {chip && <Chip className={classes.chip} variant="outlined" label={chip} />}
          </Stack>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>{children}</ExpansionPanelDetails>
      </Accordion>
    </Box>
  )
}

export default withStyles(styles)(SidePanelItem)
